import type { Permission, ScopeLevel } from './authorization';
import type { LocalizedLabel } from './localized';

/**
 * Ready-made job roles (2026-10-22).
 *
 * An administrator invites a person and picks a job title; nobody assembles permission keys by hand.
 * Each preset is the set of permissions its supported workflows need — including the lookups those
 * screens read — and nothing for convenience. They are installed on a deployment by an explicit,
 * audited, idempotent operation (`install-role-presets`), never by the demonstration seed, and an
 * installed role an administrator has since changed is reported, never overwritten.
 *
 * The separations the server enforces stay intact and are pinned by `role-presets.test.ts`:
 *
 * - **Treasury maker, approver and executor are three presets**: the collections officer requests a
 *   payment, finance management decides it, the cashier records its execution (BD-59).
 * - **Configuration is the system administrator's**; no business preset holds an administrative
 *   permission, and the administrator's preset neither approves nor moves money.
 * - **Management reads and decides; it does not bypass.** A decision permission only makes someone
 *   eligible where a published policy names it — no preset publishes a policy, and the engine still
 *   refuses self-approval on segregated operations.
 * - **Sales representatives see their own work** (`assigned` scope); the catalogue stays readable
 *   for their branch (SEC-034).
 */
export interface RolePreset {
  /** Stable role key, also the stored `roles.key`. */
  key: string;
  name: LocalizedLabel;
  /** What the person can do, in one sentence per language — shown when choosing a job title. */
  description: LocalizedLabel;
  /** The scope a new grant takes by default; an administrator may narrow or widen it. */
  defaultScope: ScopeLevel;
  permissions: readonly Permission[];
}

const DOCUMENT_READ: Permission[] = ['document.view', 'document.download'];
const DOCUMENT_ISSUE: Permission[] = [...DOCUMENT_READ, 'document.generate', 'document.upload'];
const INVENTORY_READ: Permission[] = ['inventory.project.view', 'inventory.unit.view'];
const INVENTORY_PRICED: Permission[] = [...INVENTORY_READ, 'inventory.unit.viewPricing'];
const COLLECTION_READ: Permission[] = [
  'collection.installment.view',
  'collection.receipt.view',
  'collection.instrument.view',
  'collection.reminder.view',
];
const APPROVE: Permission[] = [
  'approval.request.view',
  'approval.request.viewAmounts',
  'approval.request.approve',
  'approval.request.reject',
];
const TASKS_OWN: Permission[] = ['task.create'];
const TASKS_TEAM: Permission[] = ['task.view', 'task.create', 'task.manage'];

/** Everything a senior reader sees across the company — read-only. */
const COMPANY_READ: Permission[] = [
  ...INVENTORY_PRICED,
  'crm.customer.view',
  'crm.lead.view',
  'crm.opportunity.view',
  'sales.reservation.view',
  'sales.contract.view',
  'sales.quotation.view',
  'sales.commission.view',
  ...COLLECTION_READ,
  'collection.report.view',
  'collection.refund.view',
  'marketing.campaign.view',
  'finance.account.view',
  'finance.journal.view',
  'finance.report.view',
  'finance.invoice.view',
  'finance.budget.view',
  'finance.asset.view',
  'finance.expense.view',
  'treasury.view',
  'treasury.report.view',
  'procurement.vendor.view',
  'procurement.request.view',
  'procurement.order.view',
  'procurement.invoice.view',
  'procurement.report.view',
  'stores.view',
  'construction.view',
  'handover.view',
  'aftersales.view',
  'hr.employee.view',
  'org.view',
  'org.placement.view',
  'task.view',
  ...DOCUMENT_READ,
];

/**
 * What finance management reads (2026-10-26): money, the contracts and customers behind it, payables
 * and payroll — not leads, campaigns, construction, handover or the staff directory.
 */
const FINANCE_READ: Permission[] = COMPANY_READ.filter(
  (permission) =>
    ![
      'inventory.unit.view',
      'inventory.unit.viewPricing',
      'crm.lead.view',
      'crm.opportunity.view',
      'sales.quotation.view',
      'marketing.campaign.view',
      'stores.view',
      'construction.view',
      'handover.view',
      'aftersales.view',
      'hr.employee.view',
      'org.view',
      'org.placement.view',
      'task.view',
    ].includes(permission),
);

export const ROLE_PRESETS: readonly RolePreset[] = [
  {
    key: 'system-administrator',
    name: { ar: 'مسؤول النظام', en: 'System administrator' },
    description: {
      ar: 'يدير المستخدمين والأدوار وملف الشركة والهيكل التنظيمي والإعدادات والمشروعات والوحدات. لا يعتمد ولا يصرف أموالًا.',
      en: 'Manages users, roles, company profile, organization, settings, projects and units. Neither approves nor moves money.',
    },
    defaultScope: 'all',
    permissions: [
      'company.profile.view',
      'company.profile.manage',
      'org.view',
      'org.manage',
      'org.placement.view',
      'org.placement.manage',
      'security.account.view',
      'security.account.create',
      'security.account.suspend',
      'security.account.reactivate',
      'security.account.offboard',
      'security.account.resetPassword',
      'security.session.viewAny',
      'security.session.revokeAny',
      'security.role.view',
      'security.role.create',
      'security.role.edit',
      'security.grant.view',
      'security.grant.assign',
      'security.grant.assignAny',
      'audit.view',
      'audit.viewChanges',
      'audit.export',
      'settings.view',
      'settings.manage',
      'referenceData.manage',
      'numbering.view',
      'numbering.manage',
      'template.view',
      'template.manage',
      'approval.policy.view',
      'approval.policy.create',
      'approval.policy.publish',
      'approval.request.view',
      'operations.diagnostics',
      ...INVENTORY_PRICED,
      'inventory.project.manage',
      'inventory.unit.manage',
      'inventory.unit.import',
      'crm.lead.import',
      'crm.customer.import',
      // Archive, restore and — for records entered by mistake and referenced nowhere — delete
      // permanently (ADR-0047). Permanent deletion stays with this role alone.
      'crm.record.archive',
      'crm.record.deletePermanently',
      'inventory.record.archive',
      'inventory.record.deletePermanently',
      'finance.account.view',
      'finance.account.manage',
      'finance.period.manage',
      'finance.postingRule.manage',
      'finance.journal.view',
      'treasury.view',
      'treasury.bank.manage',
      'treasury.account.manage',
      'treasury.chequeLayout.manage',
      ...DOCUMENT_READ,
      'document.revoke',
      ...TASKS_OWN,
    ],
  },
  {
    key: 'senior-management',
    name: { ar: 'الإدارة العليا', en: 'Senior management' },
    description: {
      ar: 'يطّلع على كل أعمال الشركة وتقاريرها، ويبتّ في الموافقات التي تسنده إليها السياسات المنشورة.',
      en: "Reads the whole company's work and reports, and decides the approvals published policies assign to it.",
    },
    defaultScope: 'all',
    permissions: [...COMPANY_READ, ...APPROVE],
  },
  {
    key: 'executive-manager',
    name: { ar: 'المدير التنفيذي', en: 'Executive manager' },
    description: {
      ar: 'يتابع التشغيل اليومي في كل الأقسام، ويوزّع المهام ويتابعها، ويبتّ في الموافقات المسندة إليه.',
      en: 'Follows daily operations across departments, assigns and follows up tasks, and decides the approvals assigned to it.',
    },
    defaultScope: 'all',
    permissions: [...COMPANY_READ, ...APPROVE, ...TASKS_TEAM, 'approval.delegation.manage'],
  },
  {
    key: 'sales-manager',
    name: { ar: 'مدير المبيعات', en: 'Sales manager' },
    description: {
      ar: 'يدير فريق المبيعات: يوزّع العملاء المحتملين ويتابع الاهتمام والمتابعات والفرص والحجوزات والعقود ونتائج الفريق.',
      en: "Runs the sales team: assigns leads, follows interest, follow-ups, opportunities, reservations, contracts and the team's results.",
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_PRICED,
      'inventory.hold.create',
      'inventory.hold.manage',
      'inventory.unit.export',
      'crm.customer.view',
      'crm.customer.manage',
      'crm.customer.viewIdentity',
      'crm.customer.transfer',
      'crm.customer.merge',
      'crm.lead.view',
      'crm.lead.create',
      'crm.lead.edit',
      'crm.lead.assign',
      'crm.lead.convert',
      'crm.lead.export',
      'crm.activity.create',
      'crm.opportunity.view',
      'crm.opportunity.manage',
      'crm.opportunity.assign',
      'crm.distribution.manage',
      'crm.record.archive',
      'sales.quotation.view',
      'sales.quotation.manage',
      'sales.reservation.view',
      'sales.reservation.create',
      'sales.reservation.confirm',
      'sales.reservation.cancel',
      'sales.reservation.extend',
      'sales.contract.view',
      'sales.contract.create',
      'sales.contract.activate',
      'sales.contract.sign',
      'sales.contract.amend',
      'sales.contract.cancel',
      'sales.contract.releaseUnit',
      'sales.contract.replaceUnit',
      'sales.contract.assign',
      'sales.commission.view',
      // Payment progress is read inside the contract (its own schedule) and colleagues come from the
      // colleague picker: no collections, marketing or organization access (2026-10-26).
      ...APPROVE,
      'approval.delegation.manage',
      ...DOCUMENT_ISSUE,
      'document.archive',
      ...TASKS_TEAM,
    ],
  },
  {
    key: 'sales-representative',
    name: { ar: 'مندوب مبيعات', en: 'Sales representative' },
    description: {
      ar: 'يسجّل عملاءه المحتملين ويتابعهم ويحدّث اهتمامهم، ويعرض الوحدات والأسعار ويرفع الحجوزات وعروض الأسعار. يرى سجلاته فقط.',
      en: 'Records and follows up own leads and their interest, shows units and prices, raises reservations and quotations. Sees own records only.',
    },
    defaultScope: 'assigned',
    permissions: [
      ...INVENTORY_PRICED,
      'inventory.hold.create',
      'crm.customer.view',
      'crm.customer.manage',
      'crm.lead.view',
      'crm.lead.create',
      'crm.lead.edit',
      'crm.lead.convert',
      'crm.activity.create',
      'crm.opportunity.view',
      'crm.opportunity.manage',
      'sales.quotation.view',
      'sales.quotation.manage',
      'sales.reservation.view',
      'sales.reservation.create',
      'sales.contract.view',
      ...DOCUMENT_ISSUE,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'sales-admin',
    name: { ar: 'مسؤول CRM', en: 'Sales Admin' },
    description: {
      ar: 'يدير عمليات CRM والمبيعات في نطاقه: توزيع العملاء المحتملين والمتابعات وفريق المبيعات والتقارير والمستهدفات، ويضيف مندوبي المبيعات. لا يعتمد ولا يحرك أموالًا ولا يعدّل بيانات الوحدات.',
      en: 'Runs CRM and sales operations in scope: lead distribution, follow-ups, the sales team, reports and targets, and adds sales representatives. Neither approves nor moves money, and does not edit unit data.',
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_PRICED,
      // A reservation may start from a hold; catalogue data itself stays read-only.
      'inventory.hold.create',
      'crm.customer.view',
      'crm.customer.manage',
      'crm.lead.view',
      'crm.lead.create',
      'crm.lead.edit',
      'crm.lead.assign',
      'crm.lead.convert',
      'crm.lead.export',
      'crm.activity.create',
      'crm.opportunity.view',
      'crm.opportunity.manage',
      'crm.opportunity.assign',
      'crm.distribution.manage',
      'crm.team.view',
      'crm.team.addRepresentative',
      'crm.target.manage',
      'crm.settings.manage',
      'sales.quotation.view',
      'sales.quotation.manage',
      // The ordinary sales workflow up to a draft contract. Discount and cancellation decisions,
      // activation and everything after it stay with the people authorized for them.
      'sales.reservation.view',
      'sales.reservation.create',
      'sales.reservation.confirm',
      'sales.contract.view',
      'sales.contract.create',
      ...DOCUMENT_ISSUE,
      ...TASKS_TEAM,
    ],
  },
  {
    key: 'marketing-manager',
    name: { ar: 'مدير التسويق', en: 'Marketing manager' },
    description: {
      ar: 'يدير الحملات وأرقام أدائها ويصدّر تقاريرها، ويطّلع على العملاء المحتملين الواردين منها.',
      en: 'Manages campaigns and their performance figures, exports their reports, and reads the leads they bring.',
    },
    defaultScope: 'branch',
    permissions: [
      'marketing.campaign.view',
      'marketing.campaign.manage',
      'marketing.performance.record',
      'marketing.campaign.export',
      'crm.lead.view',
      'crm.lead.create',
      'crm.lead.import',
      'inventory.project.view',
      ...TASKS_TEAM,
    ],
  },
  {
    key: 'marketing-employee',
    name: { ar: 'موظف تسويق', en: 'Marketing employee' },
    description: {
      ar: 'يسجّل أرقام أداء الحملات ويُدخل العملاء المحتملين الواردين منها.',
      en: 'Records campaign performance figures and enters the leads they bring.',
    },
    defaultScope: 'assigned',
    permissions: [
      'marketing.campaign.view',
      'marketing.performance.record',
      'crm.lead.view',
      'crm.lead.create',
      'inventory.project.view',
      ...TASKS_OWN,
    ],
  },
  {
    key: 'finance-manager',
    name: { ar: 'المدير المالي', en: 'Finance manager' },
    description: {
      ar: 'يعتمد المدفوعات والمصروفات والسلف والعمولات والرواتب، ويرحّل القيود ويعتمد التسويات البنكية. لا ينفّذ الصرف.',
      en: 'Approves payments, expenses, advances, commissions and payroll, posts entries and approves reconciliations. Does not execute payments.',
    },
    defaultScope: 'all',
    permissions: [
      ...FINANCE_READ,
      ...APPROVE,
      'finance.journal.create',
      'finance.journal.post',
      'finance.journal.reverse',
      'finance.revenue.recognize',
      'finance.invoice.manage',
      'finance.invoice.issue',
      'finance.budget.manage',
      'finance.asset.manage',
      'finance.asset.configure',
      'finance.asset.depreciate',
      'finance.asset.dispose',
      'finance.expense.approve',
      'finance.advance.view',
      'finance.advance.approve',
      'procurement.directPurchase.approve',
      'sales.commission.approve',
      'treasury.reconciliation.approve',
      'hr.payroll.view',
      'hr.payroll.approve',
      'hr.payroll.post',
      'hr.payroll.export',
    ],
  },
  {
    key: 'accountant',
    name: { ar: 'محاسب', en: 'Accountant' },
    description: {
      ar: 'يُعدّ القيود والفواتير والإشعارات، ويجهّز التسويات البنكية، ويلغي الإيصالات المسجلة بالخطأ. لا يعتمد ولا يصرف.',
      en: 'Prepares entries, invoices and notes, prepares bank reconciliations and reverses mistaken receipts. Neither approves nor pays.',
    },
    defaultScope: 'all',
    permissions: [
      'inventory.project.view',
      'crm.customer.view',
      'sales.contract.view',
      ...COLLECTION_READ,
      'collection.receipt.cancel',
      'collection.report.view',
      'collection.refund.view',
      'finance.account.view',
      'finance.journal.view',
      'finance.journal.create',
      'finance.report.view',
      'finance.invoice.view',
      'finance.invoice.manage',
      'finance.budget.view',
      'finance.asset.view',
      'finance.expense.view',
      'treasury.view',
      'treasury.report.view',
      'treasury.reconciliation.manage',
      'procurement.vendor.view',
      'procurement.invoice.view',
      'procurement.report.view',
      ...DOCUMENT_READ,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'collections-officer',
    name: { ar: 'مسؤول التحصيل', en: 'Collections officer' },
    description: {
      ar: 'يسجّل التحصيلات والشيكات والكمبيالات ويتابع المتأخرات والتذكيرات، ويطلب صرف المستردات من الخزينة.',
      en: 'Records collections, cheques and notes, follows overdue instalments and reminders, and requests refund payments from treasury.',
    },
    defaultScope: 'branch',
    permissions: [
      'inventory.project.view',
      'crm.customer.view',
      'sales.reservation.view',
      'sales.contract.view',
      ...COLLECTION_READ,
      'collection.receipt.create',
      'collection.instrument.manage',
      'collection.reminder.manage',
      'collection.credit.apply',
      'collection.waiver.request',
      'collection.refund.view',
      'collection.refund.request',
      'collection.report.view',
      'collection.assignment.manage',
      'collection.note.create',
      'treasury.view',
      'treasury.payee.manage',
      'treasury.payment.request',
      ...DOCUMENT_ISSUE,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'cashier',
    name: { ar: 'أمين الخزينة', en: 'Cashier / treasury keeper' },
    description: {
      ar: 'يسجّل حركات النقدية والبنوك والتحويلات، وينفّذ المدفوعات المعتمدة ويطبع شيكاتها. لا يطلب ولا يعتمد.',
      en: 'Records cash and bank movements and transfers, executes approved payments and prints their cheques. Neither requests nor approves.',
    },
    defaultScope: 'branch',
    permissions: [
      'treasury.view',
      'treasury.report.view',
      'treasury.movement.record',
      'treasury.transfer.create',
      'treasury.payment.execute',
      'treasury.cheque.print',
      'collection.receipt.view',
      'collection.instrument.view',
      'collection.refund.view',
      'collection.refund.pay',
      ...DOCUMENT_READ,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'procurement-manager',
    name: { ar: 'مدير المشتريات', en: 'Procurement manager' },
    description: {
      ar: 'يدير الموردين وطلبات عروض الأسعار والترسية وأوامر الشراء، ويعتمد طلبات الشراء وفروق الجرد المسندة إليه.',
      en: 'Manages vendors, quotations, awards and purchase orders, and decides purchase requests and stock-count differences assigned to it.',
    },
    defaultScope: 'branch',
    permissions: [
      'procurement.vendor.view',
      'procurement.vendor.manage',
      'procurement.vendor.viewIdentifiers',
      'procurement.request.view',
      'procurement.rfq.manage',
      'procurement.order.view',
      'procurement.order.manage',
      'procurement.invoice.view',
      'procurement.report.view',
      'procurement.retention.release',
      'stores.view',
      'stores.adjustment.manage',
      'finance.budget.view',
      'inventory.project.view',
      ...APPROVE,
      ...DOCUMENT_ISSUE,
      ...TASKS_TEAM,
    ],
  },
  {
    key: 'procurement-officer',
    name: { ar: 'مسؤول المشتريات', en: 'Procurement officer' },
    description: {
      ar: 'يسجّل الموردين وعروض الأسعار وأوامر الشراء وفواتير الموردين، ويطلب صرف مستحقاتهم من الخزينة.',
      en: 'Records vendors, quotations, purchase orders and vendor invoices, and requests their payment from treasury.',
    },
    defaultScope: 'branch',
    permissions: [
      'procurement.vendor.view',
      'procurement.vendor.manage',
      'procurement.request.view',
      'procurement.rfq.manage',
      'procurement.order.view',
      'procurement.order.manage',
      'procurement.invoice.view',
      'procurement.invoice.record',
      'procurement.advance.request',
      'treasury.view',
      'treasury.payee.manage',
      'treasury.payment.request',
      'inventory.project.view',
      ...DOCUMENT_ISSUE,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'storekeeper',
    name: { ar: 'أمين المخزن', en: 'Storekeeper' },
    description: {
      ar: 'يستلم البضائع على أوامر الشراء، ويصرف المواد ويحوّلها، ويجرد المخزون ويسلّم العهد وينظّم التوصيل.',
      en: 'Receives goods against purchase orders, issues and transfers materials, counts stock, hands out custody and plans deliveries.',
    },
    defaultScope: 'branch',
    permissions: [
      'stores.view',
      'stores.item.manage',
      'stores.movement.record',
      'stores.count.enter',
      'stores.custody.manage',
      'stores.delivery.manage',
      'procurement.order.view',
      'procurement.receipt.record',
      'inventory.project.view',
      ...DOCUMENT_READ,
      'document.upload',
      ...TASKS_OWN,
    ],
  },
  {
    key: 'project-manager',
    name: { ar: 'مدير المشروع', en: 'Project manager' },
    description: {
      ar: 'يدير المشروعات والمباني والوحدات وعقود المقاولين، ويعتمد كميات المستخلصات وجاهزية التسليم، ويطلب المشتريات.',
      en: 'Manages projects, buildings, units and contractor contracts, approves certificate quantities and handover readiness, and requests purchases.',
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_PRICED,
      'inventory.project.manage',
      'inventory.unit.manage',
      'inventory.price.propose',
      'inventory.record.archive',
      'construction.view',
      'construction.contract.manage',
      'construction.certificate.engineeringApprove',
      'construction.progress.record',
      'procurement.request.view',
      'procurement.request.create',
      'procurement.order.view',
      'stores.view',
      'finance.budget.view',
      'handover.view',
      'handover.approve',
      ...APPROVE,
      ...DOCUMENT_ISSUE,
      ...TASKS_TEAM,
    ],
  },
  {
    key: 'site-engineer',
    name: { ar: 'مهندس الموقع', en: 'Site engineer' },
    description: {
      ar: 'يُعدّ مستخلصات المقاولين ويسجّل التقدم اليومي، ويطلب المواد، ويتابع عيوب التسليم.',
      en: 'Prepares contractor certificates, records daily progress, requests materials and follows handover defects.',
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_READ,
      'construction.view',
      'construction.certificate.prepare',
      'construction.progress.record',
      'procurement.request.view',
      'procurement.request.create',
      'stores.view',
      'handover.view',
      'handover.manage',
      ...DOCUMENT_READ,
      'document.upload',
      ...TASKS_OWN,
    ],
  },
  {
    key: 'contractor-certificate-officer',
    name: { ar: 'مسؤول المقاولين والمستخلصات', en: 'Contractor and certificate officer' },
    description: {
      ar: 'يدير عقود المقاولين ومقايساتها وضماناتها، ويطلب صرف المستخلصات المعتمدة والدفعات المقدمة من الخزينة.',
      en: 'Keeps contractor contracts, bills of quantities and guarantees, and requests treasury payment of approved certificates and advances.',
    },
    defaultScope: 'branch',
    permissions: [
      'construction.view',
      'construction.contract.manage',
      'construction.payment.request',
      'treasury.view',
      'treasury.payee.manage',
      'treasury.payment.request',
      'procurement.vendor.view',
      'inventory.project.view',
      ...DOCUMENT_ISSUE,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'hr-officer',
    name: { ar: 'مسؤول الموارد البشرية', en: 'HR officer' },
    description: {
      ar: 'يدير ملفات الموظفين وعقودهم وحضورهم وإجازاتهم وإعدادات الإجازات.',
      en: "Keeps employees' files, contracts, attendance, leave and leave settings.",
    },
    defaultScope: 'all',
    permissions: [
      'hr.employee.view',
      'hr.employee.manage',
      'hr.employee.viewSensitive',
      'hr.attendance.view',
      'hr.attendance.record',
      'hr.leave.view',
      'hr.leave.request',
      'hr.leave.configure',
      'org.view',
      'org.placement.view',
      ...DOCUMENT_READ,
      'document.upload',
      ...TASKS_OWN,
    ],
  },
  {
    key: 'payroll-officer',
    name: { ar: 'مسؤول الرواتب', en: 'Payroll officer' },
    description: {
      ar: 'يُعدّ مسيرات الرواتب ومكوّناتها وسلف الموظفين وسطور العمولات. الاعتماد والترحيل لغيره.',
      en: 'Prepares payroll runs, pay components, employee advances and commission lines. Approval and posting belong to someone else.',
    },
    defaultScope: 'all',
    permissions: [
      'hr.employee.view',
      'hr.attendance.view',
      'hr.leave.view',
      'hr.payroll.view',
      'hr.payroll.manage',
      'hr.payroll.configure',
      'hr.advance.request',
      'treasury.view',
      'treasury.payment.request',
      'sales.commission.view',
      ...DOCUMENT_READ,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'handover-officer',
    name: { ar: 'مسؤول التسليم', en: 'Handover officer' },
    description: {
      ar: 'يفتح ملفات تسليم الوحدات ويتابع بنود الجاهزية والمواعيد والعيوب، ويسجّل التسليم الموقّع.',
      en: 'Opens unit handover cases, follows readiness items, appointments and defects, and records the signed delivery.',
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_READ,
      'crm.customer.view',
      'sales.contract.view',
      'handover.view',
      'handover.manage',
      'handover.deliver',
      ...DOCUMENT_ISSUE,
      ...TASKS_OWN,
    ],
  },
  {
    key: 'customer-service',
    name: { ar: 'خدمة العملاء وما بعد البيع', en: 'Customer service / after-sales' },
    description: {
      ar: 'يستقبل طلبات العملاء وشكاواهم ويتابع حلّها، ويدير دخول العملاء إلى بوابتهم.',
      en: 'Receives customer requests and complaints and follows them to resolution, and manages customer portal access.',
    },
    defaultScope: 'branch',
    permissions: [
      ...INVENTORY_READ,
      'crm.customer.view',
      'crm.activity.create',
      'sales.contract.view',
      'handover.view',
      'aftersales.view',
      'aftersales.manage',
      'portal.manage',
      ...DOCUMENT_READ,
      ...TASKS_OWN,
    ],
  },
];

export function rolePreset(key: string): RolePreset | undefined {
  return ROLE_PRESETS.find((preset) => preset.key === key);
}

/** The widest default scope among the chosen presets — one grant carries one scope. */
export function presetDefaultScope(
  keys: readonly string[],
  order: readonly ScopeLevel[],
): ScopeLevel {
  let widest: ScopeLevel | undefined;
  for (const key of keys) {
    const preset = rolePreset(key);
    if (!preset) continue;
    if (!widest || order.indexOf(preset.defaultScope) > order.indexOf(widest)) {
      widest = preset.defaultScope;
    }
  }
  return widest ?? 'assigned';
}
